import React from 'react';
import { cn, formatNumber } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';

interface SpendingProgressBarProps {
  monthlySpend?: number;
  monthlyLimitAlert?: number;
  className?: string;
}

const SpendingProgressBar: React.FC<SpendingProgressBarProps> = ({ monthlySpend, monthlyLimitAlert, className }) => {
  const isDisabled = !monthlySpend || !monthlyLimitAlert;

  const isExceeded = !isDisabled && monthlySpend > monthlyLimitAlert;

  const isNormal = !isDisabled && !isExceeded;

  const progressPercentage = isDisabled ? 0 : Math.min((monthlySpend / monthlyLimitAlert) * 100, 100);

  const getBarColor = () => {
    if (isDisabled) return 'bg-gray-400';
    if (isExceeded) return 'bg-[#bc3229]';
    return 'bg-[#ff5b27]';
  };

  const getBgColor = () => {
    if (isDisabled) return 'bg-[#9d9da6]';
    if (isExceeded) return 'bg-[#bc3229]';
    return 'bg-[#f8d7ce]';
  };

  return (
    <div className={cn('w-full space-y-2', className)}>
      <div className="flex justify-between items-center">
        {isExceeded && (
          <>
            <div className="flex items-center text-[#bc3229]">
              <AlertCircle className="h-4 w-4 mr-1" />
              <span>Limit Exceeded</span>
            </div>
            <div className={cn('ml-auto font-medium text-[#bc3229]')}>${formatNumber(monthlySpend)}</div>
          </>
        )}
        {isNormal && (
          <div style={{ width: `${progressPercentage}%` }} className={cn('text-right font-medium text-[#ff5b27]')}>
            ${formatNumber(monthlySpend)}
          </div>
        )}
      </div>

      <div className={cn('h-6 rounded-md overflow-hidden', getBgColor())}>
        <div
          className={cn('h-full rounded-tl-md rounded-bl-md  transition-all duration-300 ease-in-out', getBarColor())}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </div>
  );
};

export default SpendingProgressBar;
